import { createEffect, createEvent, createStore, sample } from 'effector';
import { devicesApi, DeviceInDB, DeviceCreate, DeviceUpdate, DevicesListFilters, DevicesListSort } from '../api/devices';
import { $canManageDevices } from './auth';

const localTodayISO = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Параметры таблицы
export const $devicesDate = createStore<string | null>(localTodayISO()); // ISO YYYY-MM-DD
export const dateChanged = createEvent<string | null>();

$devicesDate.on(dateChanged, (_, d) => d);

// загрузка списка
export const fetchDevicesByCorpsFx = createEffect(
  async (params: {
    corpsId: string;
    date?: string | null;
    page?: number;
    pageSize?: number;
    search?: string;
    filters?: DevicesListFilters;
    sort?: DevicesListSort;
  }) => {
    const { corpsId, date, page = 1, pageSize = 10, search, filters, sort } = params;
    const data = await devicesApi.getByCorps(corpsId, {
      history_date: date || undefined,
      page,
      page_size: pageSize,
      search,
      ...(filters || {}),
      ...(sort || {}),
    });
    console.log("{ corpsId, ...data, page, pageSize }", { corpsId, ...data, page, pageSize })
    return { corpsId, ...data, page, pageSize };
  }
);

export const $devicesByCorps = createStore<Record<string, DeviceInDB[]>>({})
  .on(fetchDevicesByCorpsFx.doneData, (state, { corpsId, items }) => ({ ...state, [corpsId]: items }));

export const $devicesTotal = createStore<Record<string, number>>({})
  .on(fetchDevicesByCorpsFx.doneData, (state, { corpsId, total }) => ({ ...state, [corpsId]: total }));

export const $devicesPagination = createStore<Record<string, { page: number; pageSize: number }>>({})
  .on(fetchDevicesByCorpsFx.doneData, (state, { corpsId, page, pageSize }) => ({ ...state, [corpsId]: { page, pageSize } }));

// создание устройства
export const createDeviceFx = createEffect(async (payload: DeviceCreate) => {
  const created = await devicesApi.createDevice(payload);
  return created;
});

export const updateDeviceFx = createEffect(
  async ({ id, corpsId, data }: { id: string; corpsId: string; data: DeviceUpdate }) => {
    const updated = await devicesApi.updateDevice(id, data);
    return { corpsId, id, patch: data, updated };
  }
);

// Снятие привязки к помещению без удаления прибора и его истории.
export const detachDeviceFx = createEffect(async (id: string) => {
  await devicesApi.detachApartment(id);
  return id;
});

export const detachDeviceClicked = createEvent<string>();
export const detachDeviceErrorCleared = createEvent();
sample({
  clock: detachDeviceClicked,
  filter: $canManageDevices,
  target: detachDeviceFx,
});

// Индикатор снятия привязки.
export const $detachingDeviceIds = createStore<Set<string>>(new Set())
  .on(detachDeviceFx, (s, id) => new Set(s).add(String(id)))
  .on(detachDeviceFx.finally, (s, { params }) => {
    const next = new Set(s);
    next.delete(String(params));
    return next;
  });

export const $detachDeviceError = createStore('')
  .on(detachDeviceFx, () => '')
  .on(detachDeviceFx.failData, (_, error: any) =>
    error?.response?.data?.detail || 'Не удалось убрать прибор из квартиры'
  )
  .reset(detachDeviceErrorCleared);

// после создания — перезагрузим список по corps_id
sample({
  clock: createDeviceFx.doneData,
  source: { date: $devicesDate },
  fn: ({ date }, dev) => ({ corpsId: dev.corps_id, date, page: 1, pageSize: 10 }),
  target: fetchDevicesByCorpsFx,
});

// После снятия привязки прибор исчезает из списка текущего корпуса.
$devicesByCorps.on(detachDeviceFx.doneData, (state, id) => {
  const next: Record<string, DeviceInDB[]> = {};
  for (const key of Object.keys(state)) {
    next[key] = (state[key] || []).filter((d) => String(d.id) !== String(id));
  }
  return next;
});

$devicesByCorps.on(updateDeviceFx.doneData, (state, { corpsId, id, updated, patch }) => {
  const corpsKey = String(corpsId);
  const list = state[corpsKey];
  if (!Array.isArray(list)) return state;
  const updatedList: DeviceInDB[] = list.map((device) => {
    if (String(device.id) !== String(id)) return device;
    const merged = { ...device, ...patch, ...(updated || {}) } as DeviceInDB;
    return merged;
  });
  return {
    ...state,
    [corpsKey]: updatedList,
  };
});
